import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CoopValidators } from '@core/helpers/validators.helper';
import { ConclusioniFormValidators } from './validators/conclusioni.form-validators';
import { ConclusioniFieldValidators } from './validators/conclusioni.field-validators';
import { ConclusioniWarningsService } from './warnings/conclusioni.warnings';
import { MyVerbale } from '../../models/interfaces/revisione.interface';
import { getControl } from '@shared/helpers/form.helper';
import { ConclusioniFormListeners } from './listeners/conclusioni.form-listeners';
import { BaseRevisioneFormService } from '../../abstracts/base-revisione-form.service';
import {
    buildAmministratoreUnicoFormArray,
    buildComitatoControlloGestioneFormArray,
    buildConsiglioAmministrazioneFormArray,
    buildConsiglioGestioneFormArray,
    buildConsiglioSorveglianzaFormArray,
    buildLiquidatoriFormArray,
    buildOrganoControlloFormArray,
} from '../amm-controllo/common/helpers/norme-form-groups.helper';
import {
    buildIrregSanabiliFormArray,
    buildOsservazioniRespPrecRevFormArray,
    buildSanzioniAccessorieRilevazioneFormArray,
    buildWarningAuxFormArray,
} from './common/builders/conclusioni-form-array.builder';
import { normalizeArraysNorme } from '../amm-controllo/common/helpers/norme-normalize.helper';
import { cloneDeep } from 'lodash-es';
import { setInitialTipoSezEnte } from '../../helpers/form-store.helpers';
import { formPaths } from '@shared/constants/form-paths.const';

@Injectable()
export class ConclusioniFormService extends BaseRevisioneFormService<ConclusioniWarningsService, ConclusioniFieldValidators> {
    constructor(protected validators: ConclusioniFieldValidators, protected warningsService: ConclusioniWarningsService) {
        super(warningsService, validators);
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Stato pratica
            'my_IdAssegnazione',
            'my_consentiVerbaleSenzaBilancio',
            'my_Warning.my_ElWarning',

            // Errori ausiliari
            'auxErrors.ckFunzionamPEC',
            'auxErrors.ckScopoMutual',
            'auxErrors.tipoSezEnte',
            'auxErrors.ckNuovoSocioCatSpec',
            'auxErrors.ckObbligoBilSoc',
            'auxErrors.ckNorme',
            'auxErrors.ckNormeI',
            'auxErrors.ckNormeII',
            'auxErrors.ckNormeIII',
            'auxErrors.ckNormeIV',
            'auxErrors.ckNormeV',
            'auxErrors.ckNormeVI',
            'auxErrors.ckNormeVII',
            'auxErrors.ckNormeVIII',
            'auxErrors.ckNormeIX',
            'auxErrors.ckNormeX',
            'auxErrors.ckNormeXI',
            'auxErrors.ckNormeXII',
            'auxErrors.ckNormeXIII',
            'auxErrors.ckNormeXIV',
            'auxErrors.ckNormeXV',
            'auxErrors.ckNormeXVI',
            'auxErrors.ckNormeXVII',
            'auxErrors.ckNormeXVIII',
            'auxErrors.ckNormeXIX',
            'auxErrors.ckNormeXX',
            'auxErrors.ckLibriSociali',
            'auxErrors.ckLibriSocBolloVid',
            'auxErrors.ckLibroOrgAmm',
            'auxErrors.ckLibroOrgContr',
            'auxErrors.ckLibroGioInv',
            'auxErrors.ckLibroGioInvAgg',
            'auxErrors.tipoSezEnteII',
            'auxErrors.ckSrlAffidamContrCont',
            'auxErrors.ckSrlAffidamContrContII',
            'auxErrors.allElemCollegioSindacale',
            'auxErrors.ckSpoAffidamContrCont',
            'auxErrors.ckSpmAffidamContrCont',
            'auxErrors.ckSpdAffidamContrCont',
            'auxErrors.ckSpoAffidamContrContII',
            'auxErrors.ckSpoAffidamContrContIII',
            'auxErrors.ckSrlAffidamContrContIII',
            'auxErrors.ckSrlAffidamContrContIV',
            'auxErrors.scSocObbl',
            'auxErrors.percentSociVolont',
            'auxErrors.ckRistorni',
            'auxErrors.ckSrlAffidamContrContV',

            // Altre notizie
            `${formPaths.altreNotizie}.my_DescVertenzeGiudiz`,
            `${formPaths.altreNotizie}.my_CkVertenzeGiudiz`,
            `${formPaths.altreNotizie}.my_VertenzeGiudizImpPres`,
            `${formPaths.altreNotizie}.my_CkLibriSociali`,
            `${formPaths.altreNotizie}.my_CkLibriSocBolloVid`,
            `${formPaths.altreNotizie}.my_CkLibroOrgAmm`,
            `${formPaths.altreNotizie}.my_CkLibroOrgContr`,
            `${formPaths.altreNotizie}.my_CkLibroGioInv`,
            `${formPaths.altreNotizie}.my_CkLibroGioInvAgg`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_DataChiusura2`,
            `${formPaths.datiBilancio}.my_BilAnno1`,
            `${formPaths.datiBilancio}.my_BilAnno2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtBtot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtBIItot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtCItot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtCIItot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT1`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAtot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsDtot2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsTOT2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Passivo.my_StPaPsAI2`,

            // Raggiungimento scopo sociale
            `${formPaths.scopoSociale}.my_AttivoCirc`,
            `${formPaths.scopoSociale}.my_PassivBreve`,
            `${formPaths.scopoSociale}.my_RicaviVendAnno1Dep`,
            `${formPaths.scopoSociale}.my_RicaviVendDep`,

            // Contributo legge 59
            `${formPaths.contributoLegge59}.my_CkVersamEffett`,
            `${formPaths.contributoLegge59}.my_ImportoComplessivo`,
            `${formPaths.contributoLegge59}.my_ImportoComplessivoAnno1`,

            // Scheda coop sociali
            `${formPaths.schedaCoopSociali}.my_CkAmmessiSociFinanz`,
            `${formPaths.schedaCoopSociali}.my_CkTrattasiDiConsorzio`,
            `${formPaths.schedaCoopSociali}.my_CkServSocioSanit`,
            `${formPaths.schedaCoopSociali}.my_ckCoopSvolgeCommEqSolid`,
            `${formPaths.schedaCoopSociali}.my_ckAttSocEduPrevCommEqSolid`,
            `${formPaths.schedaCoopSociali}.my_ckCommEqSolidStrumAttSocEdu`,
            `${formPaths.schedaCoopSociali}.my_ckCommEqSolidDocumStrumAtt`,
            `${formPaths.schedaCoopSociali}.my_ScSocObbl`,
            `${formPaths.schedaCoopSociali}.my_PercentSociVolont`,

            // Effettività base sociale
            `${formPaths.baseSociale}.my_SociGiuCoopData`,
            `${formPaths.baseSociale}.my_SociSpecData`,
            `${formPaths.baseSociale}.my_SociFisCoopPrimo`,
            `${formPaths.baseSociale}.my_SociGiuCoopPrimo`,
            `${formPaths.baseSociale}.my_SociSpecPrimo`,
            `${formPaths.baseSociale}.my_CkNormeSocioLavorat`,
            `${formPaths.baseSociale}.my_UnitaSociUtil`,
            `${formPaths.baseSociale}.my_UnitaNonSociUtil`,
            `${formPaths.baseSociale}.my_ULAPrimo`,
            `${formPaths.baseSociale}.my_ULASecondo`,

            // Bilancio
            `${formPaths.bilancio}.my_RagSocRevIncaricataChoice`,
            `${formPaths.bilancio}.my_RagSocRevIncaricata`,
            `${formPaths.bilancio}.my_RagSocRevIncaricataDesc`,
            `${formPaths.bilancio}.my_AnnoPrimoEs`,
            `${formPaths.bilancio}.my_AnnoSecondoEs`,
            `${formPaths.bilancio}.my_CkRedazBilSociale`,
            `${formPaths.bilancio}.my_CkEnteTenutoCertBil`,
            `${formPaths.bilancio}.my_CkProvvedeCertBil`,
            `${formPaths.bilancio}.my_CkObbligoBilSoc`,
            `${formPaths.bilancio}.my_RagSocRevIncaricataConvenzionata`,
            `${formPaths.bilancio}.my_EnableCkEnteTenutoCertBil`,
            `${formPaths.bilancio}.my_NumBilanciInExcel`,
            `${formPaths.bilancio}.my_CkRistorni`,
            `${formPaths.bilancio}.my_CkEnteControllaSocObbRevCont`,
            `${formPaths.bilancio}.my_CkRedazBilConsol`,

            // Natura mutualistica
            `${formPaths.naturaMutualistica}.my_CkNuovoSocioCatSpec`,
            `${formPaths.naturaMutualistica}.my_CkScopoMutual`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_TotPrevalSoci`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_TotPrevalTot`,
            `${formPaths.naturaMutualistica}.my_SchedaPrevalenza.my_CkValoreQuantita`,

            // Amministrazione controllo
            `${formPaths.ammControllo}.my_CkNorme`,
            `${formPaths.ammControllo}.my_ControlloRevLegali`,
            `${formPaths.ammControllo}.my_ControlloSoci`,
            `${formPaths.ammControllo}.my_CalcObbOrganoContr`,
            `${formPaths.ammControllo}.my_ControlloRevisoreContabileSocio`,
            `${formPaths.ammControllo}.my_DataAssembleaLiquidazVolontaria`,
            `${formPaths.ammControllo}.my_AllAmminUnico`,
            `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlCoop9soci`,
            `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlCoop20soci`,
            `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlAffidamContrCont`,
            `${formPaths.ammControllo}.my_NormeSrl.my_CkSrlTipoAmministrazione`,
            `${formPaths.ammControllo}.my_NormeSrl.my_SrlScadenzaAmministratori`,
            `${formPaths.ammControllo}.my_NormeSpaDualistico.my_CkSpdAffidamContrCont`,
            `${formPaths.ammControllo}.my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza`,
            `${formPaths.ammControllo}.my_NormeSpaDualistico.my_SpdConsiglioGestione.my_SpdElemConsiglioGestione`,
            `${formPaths.ammControllo}.my_NormeSpaOrdinario.my_CkSpoAffidamContrCont`,
            `${formPaths.ammControllo}.my_NormeSpaOrdinario.my_CkSpoTipoAmministrazione`,
            `${formPaths.ammControllo}.my_NormeSpaMonistico.my_CkSpmAffidamContrCont`,
            `${formPaths.ammControllo}.my_NormeSpaMonistico.my_CkSpmTipoAmministrazione`,
            `${formPaths.ammControllo}.my_NormeSpaMonistico.my_SpmComitatoGestione.my_SpmElemComitatoGestione`,
            `${formPaths.ammControllo}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`,
            `${formPaths.ammControllo}.my_AllLiquidatori.my_AllElemLiquidatori`,
            `${formPaths.ammControllo}.my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione`,

            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,
            `${formPaths.datiIdentificativi}.my_GiorniDurataRevisione`,
            `${formPaths.datiIdentificativi}.my_Revisore`,
            `${formPaths.datiIdentificativi}.my_QualitaRappres`,
            `${formPaths.datiIdentificativi}.my_TipoSezEnte`,
            `${formPaths.datiIdentificativi}.my_ProvinciaSedeLegale`,
            `${formPaths.datiIdentificativi}.my_DataTermine`,
            `${formPaths.datiIdentificativi}.my_CkScadenzaSocieta`,
            `${formPaths.datiIdentificativi}.my_RegimeDerogatorioProvalenza`,
            `${formPaths.datiIdentificativi}.my_DataRevisioneDal`,
            `${formPaths.datiIdentificativi}.my_DataCostituz`,
            `${formPaths.datiIdentificativi}.my_TipoAdesione`,
            `${formPaths.datiIdentificativi}.my_Anno2`,
            `${formPaths.datiIdentificativi}.my_DescProvinciaSedeLegale`,
            `${formPaths.datiIdentificativi}.my_CkFunzionamPEC`,
            `${formPaths.datiIdentificativi}.my_CkMutualPrev`,
            `${formPaths.datiIdentificativi}.my_RappresentanteCoopCognome`,
            `${formPaths.datiIdentificativi}.my_RappresentanteCoopNome`,
        ]);
    }

    override initListener(): void {
        new ConclusioniFormListeners(this._form, this.store, this.toastrService).setListeners();
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_IdAssegnazione: '',
                my_consentiVerbaleSenzaBilancio: '',
                auxErrors: this.fb.group({
                    ckFunzionamPEC: '',
                    ckScopoMutual: '',
                    tipoSezEnte: '',
                    ckNuovoSocioCatSpec: '',
                    ckObbligoBilSoc: '',
                    ckNorme: '',
                    ckNormeI: '',
                    ckNormeII: '',
                    ckNormeIII: '',
                    ckNormeIV: '',
                    ckNormeV: '',
                    ckNormeVI: '',
                    ckNormeVII: '',
                    ckNormeVIII: '',
                    ckNormeIX: '',
                    ckNormeX: '',
                    ckNormeXI: '',
                    ckNormeXII: '',
                    ckNormeXIII: '',
                    ckNormeXIV: '',
                    ckNormeXV: '',
                    ckNormeXVI: '',
                    ckNormeXVII: '',
                    ckNormeXVIII: '',
                    ckNormeXIX: '',
                    ckNormeXX: '',
                    ckLibriSociali: '',
                    ckLibriSocBolloVid: '',
                    ckLibroOrgAmm: '',
                    ckLibroOrgContr: '',
                    ckLibroGioInv: '',
                    ckLibroGioInvAgg: '',
                    tipoSezEnteII: '',
                    ckSrlAffidamContrCont: '',
                    ckSrlAffidamContrContII: '',
                    allElemCollegioSindacale: '',
                    ckSpoAffidamContrCont: '',
                    ckSpmAffidamContrCont: '',
                    ckSpdAffidamContrCont: '',
                    ckSpoAffidamContrContII: '',
                    ckSpoAffidamContrContIII: '',
                    ckSrlAffidamContrContIII: '',
                    ckSrlAffidamContrContIV: '',
                    scSocObbl: '',
                    percentSociVolont: '',
                    ckRistorni: '',
                    ckSrlAffidamContrContV: '',
                }),

                my_SezioneI__Rilevazione: this.fb.group({
                    my_AltreNotizie: this.fb.group({
                        my_DescVertenzeGiudiz: '',
                        my_CkVertenzeGiudiz: '',
                        my_VertenzeGiudizImpPres: '',
                        my_CkLibriSociali: '',
                        my_CkLibriSocBolloVid: '',
                        my_CkLibroOrgAmm: '',
                        my_CkLibroOrgContr: '',
                        my_CkLibroGioInv: '',
                        my_CkLibroGioInvAgg: '',
                    }),

                    my_DatiBilancio: this.fb.group({
                        my_DataChiusura2: '',
                        my_BilAnno1: '',
                        my_BilAnno2: '',

                        my_StatoPatrimoniale__Attivo: this.fb.group({
                            my_StPaAtBtot2: '',
                            my_StPaAtBIItot2: ['', CoopValidators.required],
                            my_StPaAtCItot2: ['', CoopValidators.required],
                            my_StPaAtCIItot2: '',
                            my_StPaAtTOT1: '',
                            my_StPaAtTOT2: '',
                        }),

                        my_StatoPatrimoniale__Passivo: this.fb.group({
                            my_StPaPsAtot2: '',
                            my_StPaPsDtot2: '',
                            my_StPaPsTOT2: '',
                            my_StPaPsAI2: '',
                        }),
                    }),

                    my_RaggScopoSociale: this.fb.group({
                        my_AttivoCirc: ['', CoopValidators.required],
                        my_PassivBreve: '',
                        my_RicaviVendAnno1Dep: '',
                        my_RicaviVendDep: '',
                    }),

                    my_ContributoLegge59: this.fb.group({
                        my_CkVersamEffett: '',
                        my_ImportoComplessivo: '',
                        my_ImportoComplessivoAnno1: '',
                    }),

                    my_SchedaCoopSociali: this.fb.group({
                        my_CkAmmessiSociFinanz: '',
                        my_CkTrattasiDiConsorzio: '',
                        my_CkServSocioSanit: '',
                        my_ckCoopSvolgeCommEqSolid: '',
                        my_ckAttSocEduPrevCommEqSolid: '',
                        my_ckCommEqSolidStrumAttSocEdu: '',
                        my_ckCommEqSolidDocumStrumAtt: '',
                        my_ScSocObbl: '',
                        my_PercentSociVolont: '',
                    }),

                    my_EffettivitaBaseSociale: this.fb.group({
                        my_SociGiuCoopData: '',
                        my_SociSpecData: '',
                        my_SociFisCoopPrimo: '',
                        my_SociGiuCoopPrimo: '',
                        my_SociSpecPrimo: '',
                        my_CkNormeSocioLavorat: '',
                        my_UnitaSociUtil: '',
                        my_UnitaNonSociUtil: '',
                        my_ULAPrimo: '',
                        my_ULASecondo: '',
                    }),

                    my_Bilancio: this.fb.group({
                        my_RagSocRevIncaricataChoice: '',
                        my_RagSocRevIncaricata: '',
                        my_RagSocRevIncaricataDesc: '',
                        my_AnnoPrimoEs: '',
                        my_AnnoSecondoEs: '',
                        my_CkRedazBilSociale: '',
                        my_CkEnteTenutoCertBil: '',
                        my_CkProvvedeCertBil: '',
                        my_CkObbligoBilSoc: '',
                        my_RagSocRevIncaricataConvenzionata: '',
                        my_EnableCkEnteTenutoCertBil: '',
                        my_NumBilanciInExcel: '',
                        my_CkRistorni: '',
                        my_CkEnteControllaSocObbRevCont: '',
                        my_CkRedazBilConsol: '',
                    }),

                    my_NaturaMutualistica: this.fb.group({
                        my_CkNuovoSocioCatSpec: '',
                        my_CkScopoMutual: '',
                        my_SchedaPrevalenza: this.fb.group({
                            my_TotPrevalSoci: '',
                            my_TotPrevalTot: '',
                            my_CkValoreQuantita: '',
                        }),
                    }),

                    my_AmministrazioneControllo: this.fb.group({
                        my_CkNorme: '',
                        my_ControlloRevLegali: '',
                        my_ControlloSoci: '',
                        my_CalcObbOrganoContr: '',
                        my_ControlloRevisoreContabileSocio: '',
                        my_DataAssembleaLiquidazVolontaria: '',
                        my_AllAmminUnico: this.fb.array([]),

                        my_NormeSrl: this.fb.group({
                            my_CkSrlCoop9soci: '',
                            my_CkSrlCoop20soci: '',
                            my_CkSrlAffidamContrCont: '',
                            my_CkSrlTipoAmministrazione: '',
                            my_SrlScadenzaAmministratori: '',
                        }),

                        my_NormeSpaDualistico: this.fb.group({
                            my_CkSpdAffidamContrCont: '',

                            my_SpdConsiglioSorveglianza: this.fb.group({
                                my_SpdElemConsiglioSorveglianza: this.fb.array([]),
                            }),

                            my_SpdConsiglioGestione: this.fb.group({
                                my_SpdElemConsiglioGestione: this.fb.array([]),
                            }),
                        }),

                        my_NormeSpaOrdinario: this.fb.group({
                            my_CkSpoAffidamContrCont: '',
                            my_CkSpoTipoAmministrazione: '',
                        }),

                        my_NormeSpaMonistico: this.fb.group({
                            my_CkSpmAffidamContrCont: '',
                            my_CkSpmTipoAmministrazione: '',

                            my_SpmComitatoGestione: this.fb.group({
                                my_SpmElemComitatoGestione: this.fb.array([]),
                            }),
                        }),

                        my_AllCollegioSindacale: this.fb.group({
                            my_AllElemCollegioSindacale: this.fb.array([]),
                        }),

                        my_AllLiquidatori: this.fb.group({
                            my_AllElemLiquidatori: this.fb.array([]),
                        }),

                        my_AllConsiglioAmministrazione: this.fb.group({
                            my_AllElemConsiglioAmministrazione: this.fb.array([]),
                        }),
                    }),

                    my_DatiIdentificativi: this.fb.group({
                        my_DenominazEnte: '',
                        my_GiorniDurataRevisione: '',
                        my_Revisore: ['', CoopValidators.required],
                        my_QualitaRappres: '',
                        my_TipoSezEnte: '',
                        my_ProvinciaSedeLegale: '',
                        my_DataTermine: '',
                        my_CkScadenzaSocieta: '',
                        my_RegimeDerogatorioProvalenza: '',
                        my_DataRevisioneDal: '',
                        my_DataCostituz: '',
                        my_TipoAdesione: '',
                        my_Anno2: '',
                        my_DescProvinciaSedeLegale: '',
                        my_CkFunzionamPEC: '',
                        my_CkMutualPrev: '',
                        my_RappresentanteCoopCognome: '',
                        my_RappresentanteCoopNome: '',
                    }),

                    my_Conclusioni: this.fb.group({
                        my_WarningAux: this.fb.group({
                            my_ElWarningAux: this.fb.array([]),
                        }),
                        my_SuggConsigli: '',
                        my_IrregNonSanabili: '',
                        my_OsservControdeduz: '',
                        my_NOriginali: ['', CoopValidators.required],
                        my_DataCompilaz: '',
                        my_LuogoCompilaz: ['', CoopValidators.required],
                        my_CkIrregNonSanabili: '',
                        my_CkIrregSanabili: '',
                        my_CkProvvedimendoDi: '',
                        my_CkNominaLiq: '',
                        my_CkProposte: '',
                        my_sec6: this.fb.group({
                            my_sec7: '',
                        }),
                        my_ggDiffida: '',
                        my_IrregSanabili: this.fb.group({
                            my_ElIrregSanabili: this.fb.array([]),
                        }),
                        my_FirmaLegaleRappresentante: ['', CoopValidators.required('Inserire il nome e cognome del legale rappresentante.')],
                        my_GiudizioConclusivo: ['', CoopValidators.required],
                        my_TelefonoRevisore: '2345678',
                        my_CellulareRevisore: '456789',
                        my_IdDettaglioCellulareRevisore: '423068',
                        my_IdDettaglioTelefonoRevisore: '423068',
                        my_OraPrevistaAccertamento: '09:00:00',
                        my_LuogoPrevistoAccertamento: '',
                        my_DataScadenzaDiffida: '',
                        my_SanzioniAccessorieRilevazione: this.fb.group({
                            my_ElSanzioniAccessorieRilevazione: this.fb.array([]),
                        }),
                        my_DiffidaMinistero: '',
                        my_PECcciRevisore: '',
                        my_AltraPECRevisore: '',
                        my_gruppo34: '',
                        my_SchedaPropostaGestioneComm: this.fb.group({
                            my_ckProspettScopoSoc: '',
                            my_descProspettScopoSoc: '',
                            my_ckRisFinProsecuzioneAttSoc: '',
                            my_descRisFinProsecuzioneAttSoc: '',
                            my_descInizCommGovernativo: '',
                        }),
                        my_SchedaPropostaScioglimento: this.fb.group({
                            my_ckNonPersegScMutua: '0',
                            my_ckImpossRaggScopo: '0',
                            my_ckNoDepBil2Anni: '0',
                            my_ckNoAttiGest2Anni: '0',
                            my_descArt2545Ric: '',
                            my_ckBeniImmobili: '',
                            my_ckBeniMobiliRegistr: '',
                            my_ckValAttivoNecessLiquid: '',
                            my_descRisultUltBilApp: '',
                            my_ckPresuppostiArt223: '',
                        }),
                        my_SchedaPropostaSostLiquidCancelRegImpre: this.fb.group({
                            my_descNatRitardoLiquid: '',
                            my_descUlterMotivaz: '',
                            my_ckDepBil5Esercizi: '',
                            my_ckBeniImmob: '',
                            my_ckBeniMobRegistr: '',
                            my_ckNominaLiqAssSoci: false,
                            my_ckNominaLiqAutGiud: false,
                            my_descRisultanzeBil: '',
                            my_descNominaLiq: '',
                        }),
                        my_SchedaPropostaLiqCoatta: this.fb.group({
                            my_descMotivoProposta: '',
                            my_ckAvvIstanzaFall: '0',
                            my_ckAvvDecrIngiunt: '0',
                            my_ckAvvAzEsec: '0',
                            my_ckAvvProtesti: '0',
                            my_ckAvvAltro: '0',
                            my_descAzAvviate: '',
                            my_ckSituazPatrAggiorn: '',
                            my_descSituazPatrAggiorn: '',
                            my_ckOmessoStipContr: '',
                            my_descOmessoStipContr: '',
                            my_dtaSitPatrimAggiorn: '',
                            my_sitPatrAggTotCrediti: '',
                            my_sitPatrAggImmMat: '',
                            my_sitPatrAggRiman: '',
                            my_sitPatrAggPatrNetto: '',
                            my_sitPatrAggTotDeb: '',
                            my_descEventOss: '',
                            my_sitPatrAggTotPAss: '',
                            my_sitPatrAggDebEntrEsSucc: '',
                            my_sitPatrAggAttCirc: '',
                            my_sitPatrAggImmob: '',
                        }),
                        my_NotaProcedRevisione:
                            'La presente revisione cooperativa ha avuto per oggetto esclusivo l’accertamento delle condizioni di cui all’art. 4 del Decreto Legislativo 220/2002 ed è stata eseguita in ottemperanza alle norme statuite dal Decreto 6 dicembre 2004 del Ministero delle Imprese e del Made in Italy  e successive integrazioni e modifiche. Le procedure di revisione applicate differiscono da quelle previste dai principi di revisione contabile e pertanto non si esprime alcun giudizio sulla conformità ai principi contabili di generale accettazione dei bilanci esaminati.',
                        my_CkNoteProced: '1',
                        my_OsservazioniRespPrecRev: this.fb.group({
                            my_elOsservazioniRespPrecRev: this.fb.array([]),
                        }),
                        my_CkPresaVisOsservPrecRev: '',
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: ConclusioniFormValidators,
            }
        );
    }

    protected override beforePopulateForm(myVerbale: MyVerbale) {
        const v = normalizeArraysNorme(cloneDeep(myVerbale));
        setInitialTipoSezEnte(myVerbale, this.store);
        this._patchAll(v);
    }

    protected override afterPopulateForm(myVerbale: MyVerbale): void {
        this._patchCheckBox(myVerbale);
    }

    private _patchCheckBox(myVerbale: MyVerbale): void {
        const conclusioni = myVerbale?.my_SezioneI__Rilevazione?.my_Conclusioni;

        getControl(this._form, formPaths.conclusioni)?.patchValue(
            {
                my_SchedaPropostaScioglimento: {
                    my_ckNonPersegScMutua: conclusioni?.my_SchedaPropostaScioglimento?.my_ckNonPersegScMutua ?? null,
                    my_ckImpossRaggScopo: conclusioni?.my_SchedaPropostaScioglimento?.my_ckImpossRaggScopo ?? null,
                    my_ckNoDepBil2Anni: conclusioni?.my_SchedaPropostaScioglimento?.my_ckNoDepBil2Anni ?? null,
                    my_ckNoAttiGest2Anni: conclusioni?.my_SchedaPropostaScioglimento?.my_ckNoAttiGest2Anni ?? null,
                },
                my_SchedaPropostaSostLiquidCancelRegImpre: {
                    my_ckNominaLiqAssSoci: conclusioni?.my_SchedaPropostaSostLiquidCancelRegImpre?.my_ckNominaLiqAssSoci ?? null,
                    my_ckNominaLiqAutGiud: conclusioni?.my_SchedaPropostaSostLiquidCancelRegImpre?.my_ckNominaLiqAutGiud ?? null,
                },
                my_SchedaPropostaLiqCoatta: {
                    my_ckAvvIstanzaFall: conclusioni?.my_SchedaPropostaLiqCoatta?.my_ckAvvIstanzaFall ?? null,
                    my_ckAvvDecrIngiunt: conclusioni?.my_SchedaPropostaLiqCoatta?.my_ckAvvDecrIngiunt ?? null,
                    my_ckAvvAzEsec: conclusioni?.my_SchedaPropostaLiqCoatta?.my_ckAvvAzEsec ?? null,
                    my_ckAvvProtesti: conclusioni?.my_SchedaPropostaLiqCoatta?.my_ckAvvProtesti ?? null,
                    my_ckAvvAltro: conclusioni?.my_SchedaPropostaLiqCoatta?.my_ckAvvAltro ?? null,
                },
            },
            { emitEvent: false }
        );
    }

    private _patchAll(my_Verbale: MyVerbale): void {
        buildOrganoControlloFormArray(this.fb, my_Verbale, this._form);
        buildLiquidatoriFormArray(this.fb, my_Verbale, this._form);
        buildConsiglioAmministrazioneFormArray(this.fb, my_Verbale, this._form);
        buildAmministratoreUnicoFormArray(this.fb, my_Verbale, this._form, false);
        buildConsiglioGestioneFormArray(this.fb, my_Verbale, this._form);
        buildConsiglioSorveglianzaFormArray(this.fb, my_Verbale, this._form);
        buildComitatoControlloGestioneFormArray(this.fb, my_Verbale, this._form);
        buildIrregSanabiliFormArray(this.fb, my_Verbale, this._form);
        buildSanzioniAccessorieRilevazioneFormArray(this.fb, my_Verbale, this._form);
        buildWarningAuxFormArray(this.fb, my_Verbale, this._form);
        buildOsservazioniRespPrecRevFormArray(this.fb, my_Verbale, this._form);
    }
}
